import React from 'react';
import { array, bool, func, node, object, string } from 'prop-types';
import classNames from 'classnames';

import { propTypes } from '../../../util/types';
import { isMobile } from '../../../util/uiHelpers';
import { ListingCard, PaginationLinks } from '../../../components';
import ListingCardSkeleton from '../../../components/ListingCardSkeleton/ListingCardSkeleton';

import css from './SearchResultsPanel.module.css';

/**
 * SearchResultsPanel component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {Array<propTypes.listing>} props.listings - The listings
 * @param {propTypes.pagination} props.pagination - The pagination
 * @param {Object} props.search - The search
 * @param {Function} props.setActiveListing - The function to handle the active listing
 * @param {boolean} [props.isMapVariant] - Whether the map variant is enabled
 * @param {string} [props.listingTypeParam] - The listing type path parameter
 * @param {Object} props.intl - The intl object for translations
 * @param {propTypes.currentUser} [props.currentUser] - The current user (PCIFIC)
 * @param {Function} [props.onUpdateFavorites] - The function to update favorites/wishlist (PCIFIC)
 * @returns {JSX.Element}
 */
const SearchResultsPanel = props => {
  const {
    children = null,
    className = null,
    rootClassName = null,
    listings = [],
    pagination = null,
    search = null,
    setActiveListing,
    isMapVariant = true,
    listingTypeParam = null,
    intl = null,
    currentUser = null,
    onUpdateFavorites = null,
    searchInProgress = false,
    viewMode = 'grid',
    annotations = [],
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const pageName = listingTypeParam ? 'SearchPageWithListingType' : 'SearchPage';

  const paginationLinks =
    pagination && pagination.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName={pageName}
        pagePathParams={{ listingType: listingTypeParam }}
        pageSearchParams={search}
        pagination={pagination}
        aria-label={intl.formatMessage({ id: 'SearchResultsPanel.screenreader.pagination' })}
      />
    ) : null;

  const shouldUseMobileList = !isMapVariant && isMobile();
  const effectiveViewMode = shouldUseMobileList ? 'list' : viewMode;
  const isListMode = effectiveViewMode === 'list';

  const cardRenderSizes = isMapVariant => {
    if (isListMode) {
      // List mode: image is fixed ~200px, but srcset should cover retina
      return ['(max-width: 549px) 140px', '200px'].join(', ');
    } else if (isMapVariant) {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 767px) 100vw',
        `(max-width: 1023px) ${panelMediumWidth}vw`,
        `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
        `${panelLargeWidth / 3}vw`,
      ].join(', ');
    } else {
      // Grid within 1400px container minus 240px sidebar minus 24px gap
      // Content area ~1088px max: 2→3→3→4→4 columns across breakpoints
      return [
        '(max-width: 549px) 50vw',
        '(max-width: 1279px) 33vw',
        '260px',
      ].join(', ');
    }
  };

  // Show skeleton loaders when searching and no listings yet
  const showSkeletons = searchInProgress && listings.length === 0;
  const skeletonCount = isMapVariant ? 4 : 8;

  return (
    <div className={classes}>
      <ul
        className={
          isMapVariant
            ? css.listingCardsMapVariant
            : isListMode
            ? css.listingCardsList
            : css.listingCards
        }
      >
        {showSkeletons
          ? Array.from({ length: skeletonCount }).map((_, i) => (
              <li key={`skeleton-${i}`} className={css.resultItem}>
                <ListingCardSkeleton className={css.listingCard} staggerIndex={i} />
              </li>
            ))
          : listings.map((l, i) => (
              <li key={l.id.uuid} className={css.resultItem} data-listing-id={l.id.uuid}>
                <ListingCard
                  className={css.listingCard}
                  listing={l}
                  renderSizes={cardRenderSizes(isMapVariant)}
                  setActiveListing={setActiveListing}
                  currentUser={currentUser}
                  onUpdateFavorites={onUpdateFavorites}
                  staggerIndex={i}
                  variant={isListMode ? 'list' : 'grid'}
                  annotation={annotations.find(a => a.listing_id === l.id.uuid)}
                />
              </li>
            ))}
        {children}
      </ul>
      {paginationLinks}
    </div>
  );
};

SearchResultsPanel.propTypes = {
  children: node,
  className: string,
  listings: array,
  pagination: propTypes.pagination,
  rootClassName: string,
  search: object,
  isMapVariant: bool,
  listingTypeParam: string,
  intl: object,
  currentUser: propTypes.currentUser,
  onUpdateFavorites: func,
  searchInProgress: bool,
  viewMode: string,
  annotations: array,
};

export default SearchResultsPanel;
